import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {navigator} from 'lit-element-router';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {state} from 'lit/decorators/state.js';
import {when} from 'lit/directives/when.js';
import {choose} from 'lit/directives/choose.js';
import {repeat} from 'lit/directives/repeat.js';
import {classMap} from 'lit/directives/class-map.js';
import {msg} from '@lit/localize';
import store from '../../store';
import {setMedia} from '../../slices/statusSlice';
import {downloadPDF, downloadPNG} from '../../slices/downloadSlice';

import loading from '../../../icons/loading';
import specsheet from '../../../icons/specsheet';
import checkmark from '../../../icons/checkmark';
import copy from '../../../icons/copy';
import plus from '../../../icons/plus';
import edit from '../../../icons/arrow-alt-left';
import reset from '../../../icons/reset';
import {selectIsOnesided, selectLdt1, selectLdt2, selectMountingInstruction} from '../../selectors';
import {routeUrl} from '../../util/routeUrl';

@customElement('specsheet-panel')
export class SpecsheetPanel extends connect(store)(navigator(LitElement)) {

	static styles = css`
		:host {
			height: calc(100% - 68px);
			display: flex;
			flex-direction: row;
			gap: var(--large-padding);
			padding: 0 var(--large-padding);
		}

		.actions {
			display: flex;
			flex-direction: column;
			flex-grow: 1;
			min-width: 320px;
			margin-bottom: auto;
			animation: show 0.4s ease-out 0s 1 normal forwards;

		}

		@media (max-width: 768px) {
			:host {
				flex-direction: column;
			}
		}

		.action-headline {
			font: var(--font-widget-title);
			color: var(--color-widget-text);
			padding-bottom: 12px;
		}

		.action-items {
			display: flex;
			flex-direction: column;
			padding: var(--small-padding);
			background-color: var(--color-grey6);
			margin-bottom: var(--small-padding);
		}

		.button {
			text-align: left;
			display: flex;
			font: var(--font-widget-label);
			color: var(--color-widget-text);
			margin: 5px 0;
			text-transform: uppercase;
			letter-spacing: 1px;
			transition: background-color 0.4s, color 0.4s;
			background-color: transparent;
			border: none;
			text-decoration: none;
			cursor: pointer;
			padding: 0;
		}

		.button.loading {
			pointer-events: none;
			opacity: 0.7;
		}

		.subtitle {
			font-family: var(--font-family-light);
			font-size: var(--font-size-widget);
			color: var(--color-text-widget-title);
			text-transform: uppercase;
			letter-spacing: 1px;
		}

		.links {
			font-family: var(--font-family-light);
			font-size: var(--font-size-widget);
			color: var(--color-text-widget-title);
			text-transform: uppercase;
			letter-spacing: 1px;
		}

		.link {
			color: var(--color-widget-text);
			padding-left: var(--very-small-padding);
		}

		.pages {
			padding-top: 0;
			max-width: var(--specsheet-maxwidth);
			margin: 0 auto;
			overflow-y: scroll;
			animation: show 0.4s ease-out 0s 1 normal forwards;
		}

		.icon {
			display: inline-block;
			height: 16px;
			width: 16px;
			margin-right: 10px;
			color: var(--color-widget-text);
		}

		.icon svg {
			width: 100%;
			height: auto;
			fill: currentColor !important;
		}

		.icon svg * {
			stroke: currentColor !important;
		}

		.strong {
			font-family: var(--font-family-bold);
		}

		@keyframes show {
			from {
				opacity: 0;
				margin-top: 40px;
			}

			to {
				opacity: 100%;
				margin-top: 0;
			}
		}
    `;

	@property({type: Boolean})
	active = false;

	@state({type: Boolean})
	tenderTextCopied = false;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(s) {
		this.tenderTextCopied = false;
		this.state = s;
		this.status = s.status;
		this.lang = s.status.lang;
		this.active = s.status.specsheetActive;
		this.downloadStatus = s.download.status;
		this.downloadPngStatus = s.download.statusPng;
		this.relux1 = s.productData.relux1;
		this.relux2 = s.productData.relux2;
	}

	connectedCallback() {
		super.connectedCallback();
		addEventListener('keydown', this.handleKeydown);
	}

	handleKeydown = (e) => {
		switch (e.keyCode) {
			case 27: // ESC
				if (this.active) {
					this.closeSpecsheet();
				}
				break;
		}
	};

	closeSpecsheet(e) {
		store.dispatch(setMedia({media: 'sections'}));
	}

	downloadPDF(e) {
		store.dispatch(downloadPDF({type: 'detail'}));
	}

	downloadPNG(e) {
		store.dispatch(downloadPNG());
	}

	editConfiguration(e) {
		this.closeSpecsheet();
	}

	startNewConfiguration(e) {
		this.closeSpecsheet();
		this.navigate(routeUrl({media: 'sections', key: 'new'}));
		window.location.reload();
	}

	copyTendertext(e) {
		const event = new CustomEvent('copyToClipboard', {
			bubbles: true,
			composed: true
		});
		this.dispatchEvent(event);
		this.tenderTextCopied = true;
	}
	hasRelux1() {
		return this.relux1 !== null && this.relux1.length > 0;
	}
	hasRelux2() {
		return this.relux2 !== null && this.relux2.length > 0;
	}

	render() {
		return html`
			<div class="actions">

				<div class="action-headline">
					Downloads
				</div>
				<div class="action-items">
					<button class="button ${classMap({loading: this.downloadStatus === 'loading'})}" @click="${this.downloadPDF}">
						<span class="icon">${this.downloadStatus === 'loading' ? loading : specsheet}</span>
						<span class="label strong">${msg('Download PDF')}</span>
					</button>
					<button class="button ${classMap({loading: this.downloadPngStatus === 'loading'})}" @click="${this.downloadPNG}">
						<span class="icon">${this.downloadPngStatus === 'loading' ? loading : specsheet}</span>
						<span class="label">${msg('Download Product Image (png)')}</span>
					</button>
					${when(selectLdt1(this.state) !== null, () => html`
						<a class="button" href="${selectLdt1(this.state)}" target="_blank">
							<span class="icon">${specsheet}</span>
							<span class="label">${msg('LDT Side')} A</span>
						</a>
					`)}
					${when(!selectIsOnesided(this.state) && selectLdt2(this.state) !== null, () => html`
						<a class="button" href="${selectLdt2(this.state)}" target="_blank">
							<span class="icon">${specsheet}</span>
							<span class="label">${msg('LDT Side')} B</span>
						</a>
					`)}
					${when(selectMountingInstruction(this.state) !== null, () => html`
						<a class="button" href="${selectMountingInstruction(this.state)}" target="_blank">
							<span class="icon">${specsheet}</span>
							<span class="label">${msg('Mounting instruction')}</span>
						</a>
					`)}
				</div>

				${when(this.hasRelux1() || this.hasRelux2(), () => html`
					<div class="action-headline">
						${msg('Technical Data')}
					</div>
					<div class="action-items">
							${when(this.hasRelux1(), () => html`
								<div class="links">
									A:
									${repeat(this.relux1, item => item.type, item => html`
										<a class="link" target="_blank" href="${item.url}">${item.type}</a>
									`)}
								</div>
							`)}
							${when(this.hasRelux2(), () => html`
								<div class="links">
									B:
									${repeat(this.relux2, item => item.type, item => html`
										<a class="link" target="_blank" href="${item.url}">${item.type}</a>
									`)}
								</div>
							`)}
					</div>
				`)}

				<div class="action-headline">
					Tools
				</div>
				<div class="action-items">
					<button class="button" @click="${this.copyTendertext}">
						<span class="icon">${this.tenderTextCopied ? checkmark : copy}</span>
						<span class="label">${msg('Copy Tendertext to Clipboard')}</span>
					</button>
					<button class="button" @click="${this.editConfiguration}">
						<span class="icon">${edit}</span>
						<span class="label">${msg('Edit this configuration')}</span>
					</button>
					<!--
					<button class="button" @click="${this.duplicateConfiguration}">
						<span class="icon">${plus}</span>
						<span class="label">${msg('Copy this configuration')}</span>
					</button>
					-->
					<button class="button" @click="${this.startNewConfiguration}">
						<span class="icon">${reset}</span>
						<span class="label">${msg('Start new configuration')}</span>
					</button>
				</div>
			</div>
			<div class="pages">
				<shared-specsheet></shared-specsheet>
			</div>
		`;
	}
}
